"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldAlert, Flame, Cpu, Box, CircleCheck as CheckCircle2, ArrowRight, Sparkles, Gauge } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const productFamilies = [{
  id: "atex-spectrometers",
  title: "ATEX-Rated Process Spectrometers",
  badge: "Zone 0 / 1 / 20 / 21",
  icon: Flame,
  description: "Continuous in-line and at-line optical spectrometers certified for explosive dust and gas atmospheres in pharmaceutical and chemical plants.",
  features: ["Direct integration with blenders and dryers", "ATEX and IECEx certified housings", "Real-time blend uniformity and moisture tracking"],
  primaryLink: "/ProcessAnalyticalTechnologyPage",
  primaryLabel: "Explore PAT Analyzers"
}, {
  id: "intrinsically-safe",
  title: "Intrinsically Safe Handheld Devices",
  badge: "Zone 1 / Class I Div 1",
  icon: ShieldAlert,
  description: "Rugged handheld Raman and NIR analyzers engineered to prevent ignition during raw material verification and hazardous chemical screening.",
  features: ["Immediate through-barrier scanning", "Certified low-power optical assemblies", "Military-grade sealed IP68 enclosures"],
  primaryLink: "/RamanHandheldAnalyzersPage",
  primaryLabel: "View Handheld Systems"
}, {
  id: "pressurized-enclosures",
  title: "Pressurized Enclosure & Purge Systems",
  badge: "Ex p Protection",
  icon: Box,
  description: "Custom Ex p purge containment cabinets and integration units that allow standard analytical instruments to safely run in classified spaces.",
  features: ["Automatic positive-pressure regulation", "Continuous purge air flow monitoring", "Integrated emergency interlocks"],
  primaryLink: "/PatInstallationIntegrationPage",
  primaryLabel: "View Enclosure Options"
}];
export default function HazardousFamilies() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="hazardousareaproductfamilies" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3.5 px-3 py-1 font-medium">
            <Gauge className="size-3.5 mr-1 text-primary" />
            Classified Environment Solutions
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Hazardous Area Product Families
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Standardised instrumentation and protective enclosures engineered to deliver analytical accuracy while ensuring compliance with stringent ATEX, IECEx, and UKEX regulations.
          </p>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {productFamilies.map((family, index) => {
          const IconComponent = family.icon;
          return <motion.div key={family.id} data-index={index} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full border border-border/80 bg-card hover:border-primary/50 transition-colors shadow-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div className="size-12 rounded-lg bg-secondary text-primary flex items-center justify-center border border-border">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs font-semibold px-2.5 py-0.5 border-border bg-background">
                        {family.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground tracking-tight">
                      {family.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-normal mt-2">
                      {family.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 flex-1">
                    <div className="border-t border-border/60 pt-4 mt-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                        Key Capabilities
                      </div>
                      <ul className="space-y-2.5 text-sm text-card-foreground">
                        {family.features.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-4 border-t border-border/40">
                    <Button asChild variant="outline" className="w-full justify-between group">
                      <Link to={family.primaryLink}>
                        <span>{family.primaryLabel}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 text-primary" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Bottom Consultation Banner */}
        <div className="mt-12 p-6 rounded-xl bg-secondary border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-background text-primary border border-border shrink-0">
              <Sparkles className="size-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Need verification for specific hazardous zone classifications?
              </h3>
              <p className="text-sm text-muted-foreground">
                Our application specialists help determine ATEX zoning compatibility and optical barrier requirements.
              </p>
            </div>
          </div>
          <Button asChild variant="default" className="shrink-0 w-full md:w-auto">
            <Link to="/DiscussYourApplicationPage">
              Discuss Your Application
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}